import React from "react";
import { Updater, useImmer } from "use-immer";

import { IgButton, IgTextField, IgTypography } from "@intelligems/ig-theme";
import {
  BaseIntegrationDto,
  GoogleGlobalSettingsDto,
} from "@intelligems/ig-types";
import { Grid, Skeleton } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";

import { useCleanupGa } from "../../../../api/google/cleanup-ga";
import { useGetGaAccounts } from "../../../../api/google/get-ga-accounts";
import { usePutGaIntegration } from "../../../../api/google/put-ga-integration";
import {
  gaKeys,
  integrationKeys,
  organizationsKeys,
} from "../../../../api/queryKeys";
import { AreYouSure } from "../../../../components/Popover/AreYouSure";
import { IgAlert } from "../../../../components/Utils/IgAlert";

import { GoogleAnalyticsAccount } from "./GoogleAnalyticsAccount";

interface GoogleAnalyticsProps {
  googleIntegration: BaseIntegrationDto<GoogleGlobalSettingsDto>;
  globalSettings: GoogleGlobalSettingsDto;
  setGlobalSettings: Updater<GoogleGlobalSettingsDto>;
}

const TrackingMessage = ({ measurementId }: { measurementId: string }) => {
  const cleanupGa = useCleanupGa();

  return (
    <Grid
      container
      direction={"row"}
      item
      columnSpacing={2}
      sx={{ marginBottom: "-20px" }}
      alignItems={"center"}
      justifyContent={"space-between"}
    >
      <Grid container item xs={6}>
        <Grid item>
          <IgTypography>Tracking data in GA4 ({measurementId})</IgTypography>
        </Grid>
      </Grid>
    </Grid>
  );
};

const SelectMeasurementId = ({
  globalSettings,
  setGlobalSettings,
  googleIntegration,
}: GoogleAnalyticsProps) => {
  const putGaIntegration = usePutGaIntegration();
  const [isLoading, setIsLoading] = useImmer<boolean>(false);
  const [search, setSearch] = useImmer("");

  const queryClient = useQueryClient();
  const gaAccounts = useGetGaAccounts(Boolean(googleIntegration));

  if (gaAccounts.isError) {
    return (
      <IgAlert
        severity={"error"}
        title={"Insufficient Permissions"}
        message={
          "Oops! Google says this account does not have sufficient permissions to access Google Analytics.  Please try logging in with Google again.  If this problem persists, please have your Google account administrator log in, or reach out to our support."
        }
      />
    );
  }

  if (gaAccounts.isPending) {
    return (
      <Grid item sx={{ width: "100%" }}>
        <Skeleton
          sx={{
            width: "100%",
            height: "150px",
          }}
        />
      </Grid>
    );
  }

  const onSave = () => {
    setIsLoading(true);
    putGaIntegration.mutate(
      {
        body: {
          globalSettings: {
            ga: {
              accountId: globalSettings.ga?.accountId ?? "",
              propertyId: globalSettings.ga?.propertyId ?? "",
              streamId: globalSettings.ga?.streamId ?? "",
              measurementId: globalSettings.ga?.measurementId ?? "",
            },
          },
        },
        pathParams: undefined,
        queryParams: undefined,
      },
      {
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: integrationKeys.all,
          });
          await queryClient.invalidateQueries({ queryKey: gaKeys.all });
          await queryClient.invalidateQueries({
            queryKey: organizationsKeys.all,
          });
        },
        onSettled: () => {
          setIsLoading(false);
        },
      }
    );
  };

  return (
    <Grid container item direction={"column"} rowSpacing={1}>
      <Grid item sx={{ marginBottom: "10px" }}>
        <IgAlert
          severity={"error"}
          title={"Measurement ID Required"}
          message={
            "Google Analytics will not work without a Measurement ID. Please select below."
          }
        />
      </Grid>
      <Grid item sx={{ marginBottom: "10px" }}>
        <IgTextField
          value={search}
          sx={{ width: "45%" }}
          placeholder={"Search By Account Name"}
          onChange={(e) => {
            setSearch(e.target.value || "");
          }}
        />
      </Grid>
      {gaAccounts.isSuccess &&
        (gaAccounts.data.accounts || [])
          .filter((account) =>
            search.length > 0
              ? account.displayName?.toLowerCase()?.includes(search) ||
                account.account?.includes(search)
              : account
          )
          .map((account, index) => (
            <GoogleAnalyticsAccount
              key={`account-${index}`}
              account={account}
              index={index}
              globalSettings={globalSettings}
              setGlobalSettings={setGlobalSettings}
            />
          ))}

      <Grid
        item
        sx={{
          marginTop: "10px",
        }}
      >
        <IgButton
          disabled={false}
          loading={isLoading}
          variant={"contained"}
          onClick={onSave}
        >
          Save
        </IgButton>
      </Grid>
    </Grid>
  );
};

export const GoogleAnalyticsStatusSection = ({
  googleIntegration,
  globalSettings,
  setGlobalSettings,
}: GoogleAnalyticsProps) => {
  return (
    <Grid item container direction={"column"} rowSpacing={2}>
      {googleIntegration.globalSettings?.ga?.measurementId ? (
        <TrackingMessage
          measurementId={googleIntegration.globalSettings?.ga?.measurementId}
        />
      ) : (
        <SelectMeasurementId
          googleIntegration={googleIntegration}
          globalSettings={globalSettings}
          setGlobalSettings={setGlobalSettings}
        />
      )}
    </Grid>
  );
};
